import { useEffect, useState } from 'react'
import { Pencil, Plus, X } from 'lucide-react'
import {
  savePartnerLegalEntity,
  updatePartnerLegalEntityDetails,
} from '@/services/royaltyService'
import type { PartnerLegalEntity } from '@/types'

const inputCls = 'w-full text-xs border border-border rounded px-2 py-1.5 bg-background disabled:opacity-60'

/** Страны, где у сети уже есть юрлица франчайзи (подсказки, ввод свободный). */
const COUNTRY_SUGGESTIONS = [
  'Россия',
  'Беларусь',
  'Казахстан',
  'Киргизия',
  'Армения',
  'Молдова',
  'Польша',
  'ОАЭ',
]

/**
 * Страна по умолчанию для нового юрлица: у партнёра уже заведены юрлица одной страны — берём её.
 * Разнобой (или юрлиц нет) — решает вызывающий, дефолт формы «Россия». Своей копии этого правила
 * в родителях не заводить: иначе форма договора и форма ДС начнут предлагать разные страны.
 */
export function resolveDefaultEntityCountry(
  entities: Array<{ country?: string | null }>
): string | undefined {
  const countries = new Set(
    entities.map(e => e.country?.trim()).filter((value): value is string => !!value)
  )
  return countries.size === 1 ? [...countries][0] : undefined
}

/**
 * Инлайн-заведение нового юрлица партнёра прямо в пикере (без ухода на экран партнёра).
 * Создаёт строку через lawyer-gated RPC в savePartnerLegalEntity (тот же путь, что и в
 * очереди разноски). После создания зовёт onCreated с новой строкой — родитель добавляет
 * её в список и выбирает.
 *
 * ⚠️ Страна вводится здесь, а не берётся дефолтом «Россия»: она уезжает в реквизиты стороны
 * договора, а у нерезидента налоговый номер называется иначе (УНП РБ — 9 цифр, БИН РК — 12).
 * defaultCountry родитель считает по уже заведённым юрлицам партнёра.
 */
export function AddPartnerEntityInline({
  partnerNumber,
  onCreated,
  disabled,
  defaultCountry = 'Россия',
}: {
  partnerNumber: number
  onCreated: (entity: PartnerLegalEntity) => void
  disabled?: boolean
  defaultCountry?: string
}) {
  const [open, setOpen] = useState(false)
  const [legalName, setLegalName] = useState('')
  const [inn, setInn] = useState('')
  const [country, setCountry] = useState(defaultCountry)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setLegalName('')
    setInn('')
    setCountry(defaultCountry)
    setError(null)
  }

  async function handleCreate() {
    const name = legalName.trim()
    if (!name) {
      setError('Укажите название юрлица')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const created = await savePartnerLegalEntity({
        partner_number: partnerNumber,
        legal_name: name,
        inn: inn.trim() || null,
        country: country.trim() || defaultCountry,
      })
      onCreated(created)
      reset()
      setOpen(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  if (disabled) return null

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:opacity-80"
      >
        <Plus size={12} /> Добавить юрлицо франчайзи
      </button>
    )
  }

  return (
    <div className="mt-2 rounded-md border border-dashed border-primary/50 p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium">Новое юрлицо франчайзи</span>
        <button
          type="button"
          onClick={() => {
            reset()
            setOpen(false)
          }}
          className="text-muted-foreground hover:text-foreground"
        >
          <X size={13} />
        </button>
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground">Название юрлица</label>
        <input
          type="text"
          value={legalName}
          onChange={e => setLegalName(e.target.value)}
          placeholder="ООО «…» / ИП …"
          className={inputCls}
          autoFocus
        />
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground">Страна</label>
        <input
          type="text"
          list="partner-entity-country-suggestions"
          value={country}
          onChange={e => setCountry(e.target.value)}
          placeholder="Россия"
          className={inputCls}
        />
        <datalist id="partner-entity-country-suggestions">
          {COUNTRY_SUGGESTIONS.map(item => (
            <option key={item} value={item} />
          ))}
        </datalist>
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground">
          Налоговый номер (необязательно)
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={inn}
          onChange={e => setInn(e.target.value.replace(/[^\d]/g, ''))}
          placeholder="ИНН РФ 10 или 12 цифр, УНП РБ — 9"
          className={inputCls}
        />
        <p className="text-[10px] text-muted-foreground mt-0.5">
          Только налоговый номер. Номер счёта (IBAN) сюда не вносить — он уходит в назначение
          платежа как ИНН.
        </p>
      </div>
      {error && <div className="text-[11px] text-destructive">{error}</div>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            reset()
            setOpen(false)
          }}
          className="text-[11px] text-muted-foreground"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={saving || !legalName.trim()}
          className="text-[11px] bg-primary text-primary-foreground px-3 py-1.5 rounded-lg disabled:opacity-50"
        >
          {saving ? 'Создание…' : 'Создать и выбрать'}
        </button>
      </div>
    </div>
  )
}

/**
 * Правка реквизитов уже выбранного юрлица прямо в карточке ДКК: название, страна и
 * налоговый номер, если он ещё не заполнен.
 *
 * ⚠️ Заведено потому, что заполнить номер было негде вовсе: во фронте существовал
 * только INSERT (`savePartnerLegalEntity`), а завести второе юрлицо с тем же номером
 * не даёт глобальная уникальность ИНН — то есть строку вида «Юрлицо 024506851969»
 * или ИП без номера исправить было нечем.
 *
 * ⚠️ Уже заполненный номер здесь только показывается: другой номер — другое лицо,
 * то есть смена стороны договора (новое юрлицо + ДС «Сменить юрлицо»), а не правка
 * реквизита. Тот же запрет продублирован в сервисе — он и решает.
 */
export function EditPartnerEntityInline({
  entity,
  onUpdated,
  disabled,
}: {
  entity: PartnerLegalEntity
  onUpdated: (entity: PartnerLegalEntity) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [legalName, setLegalName] = useState(entity.legal_name ?? '')
  const [country, setCountry] = useState(entity.country ?? '')
  const [inn, setInn] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentInn = entity.inn?.trim() || ''
  const hasInn = currentInn.length > 0

  // Форма открывается на конкретном юрлице: при смене выбора в пикере поля обязаны
  // перечитаться, иначе сохранится название предыдущей стороны.
  useEffect(() => {
    setLegalName(entity.legal_name ?? '')
    setCountry(entity.country ?? '')
    setInn('')
    setError(null)
    setOpen(false)
  }, [entity.entity_id, entity.legal_name, entity.country, entity.inn])

  async function handleSave() {
    const name = legalName.trim()
    if (!name) {
      setError('Укажите название юрлица')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const updated = await updatePartnerLegalEntityDetails(
        entity.entity_id,
        { legal_name: name, country: country.trim() || null, inn: hasInn ? currentInn : inn.trim() || null },
        { partner_number: entity.partner_number, inn: entity.inn }
      )
      onUpdated(updated)
      setOpen(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  if (disabled) return null

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 ml-3 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:opacity-80"
      >
        <Pencil size={11} /> Исправить реквизиты юрлица
      </button>
    )
  }

  return (
    <div className="mt-2 rounded-md border border-dashed border-primary/50 p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium">Реквизиты юрлица</span>
        <button
          type="button"
          onClick={() => {
            setLegalName(entity.legal_name ?? '')
            setCountry(entity.country ?? '')
            setInn('')
            setError(null)
            setOpen(false)
          }}
          className="text-muted-foreground hover:text-foreground"
        >
          <X size={13} />
        </button>
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground">Название юрлица</label>
        <input
          type="text"
          value={legalName}
          onChange={e => setLegalName(e.target.value)}
          placeholder="ООО «…» / ИП …"
          className={inputCls}
          autoFocus
        />
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground">Страна</label>
        <input
          type="text"
          list="partner-entity-country-suggestions"
          value={country}
          onChange={e => setCountry(e.target.value)}
          placeholder="Россия"
          className={inputCls}
        />
        <datalist id="partner-entity-country-suggestions">
          {COUNTRY_SUGGESTIONS.map(item => (
            <option key={item} value={item} />
          ))}
        </datalist>
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground">Налоговый номер</label>
        {hasInn ? (
          <>
            <input type="text" value={currentInn} readOnly className={`${inputCls} opacity-70`} />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Номер уже заполнен и здесь не меняется. Другой номер — это другое лицо: заведите
              новое юрлицо и оформите ДС «Сменить юрлицо».
            </p>
          </>
        ) : (
          <>
            <input
              type="text"
              inputMode="numeric"
              value={inn}
              onChange={e => setInn(e.target.value.replace(/[^\d]/g, ''))}
              placeholder="ИНН РФ 10 или 12 цифр, УНП РБ — 9"
              className={inputCls}
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Только налоговый номер. Номер счёта (IBAN) сюда не вносить — он уходит в назначение
              платежа как ИНН.
            </p>
          </>
        )}
      </div>
      {error && <div className="text-[11px] text-destructive">{error}</div>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setLegalName(entity.legal_name ?? '')
            setCountry(entity.country ?? '')
            setInn('')
            setError(null)
            setOpen(false)
          }}
          className="text-[11px] text-muted-foreground"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !legalName.trim()}
          className="text-[11px] bg-primary text-primary-foreground px-3 py-1.5 rounded-lg disabled:opacity-50"
        >
          {saving ? 'Сохранение…' : 'Сохранить'}
        </button>
      </div>
    </div>
  )
}
